import React, { useRef } from 'react';
import { usePaginatedQueryV8 } from 'motion-components';
import { useRootStore } from 'store';
import { useHandleError } from 'shared-pages/services';

import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';

import { GridPage } from './grid';
import { gqlGetRoutes } from './graphql';
import { useOnRouteChange } from 'shared-pages/hooks/common/useOnRouteChange';
import LoadingOverlay from '@achmadk/react-loading-overlay';

export function buildWhereFromFilterValues(values): string {
  let where: any = {};
  if (values) {
    if (values.statusId) {
      where.statusId = values.statusId;
    }
    if (values.collectorId) {
      where.collectorId = values.collectorId;
    }
  }
  return JSON.stringify(where);
}

export const RouteGridPage = observer(
  ({
    collectorId,
    statusId = 1,
    collectionOrderId,
    onClose,
  }: {
    collectorId?: number;
    statusId?: number;
    collectionOrderId?: number;
    onClose: any;
  }) => {
    const { appStore } = useRootStore();
    const { handleGraphqlError } = useHandleError('RouteGridPage');
    useOnRouteChange(() => {
      onClose();
    });
    const { t } = useTranslation();

    const initOrderBy = 'routeDate DESC';
    const query = usePaginatedQueryV8(
      gqlGetRoutes,
      () => buildWhereFromFilterValues({ statusId, collectorId }),
      initOrderBy,
      appStore,
      true,
      { statusId, collectorId },
      false,
      'modal_route_grid',
    );

    const {
      loading,
      data,
      dataCount,
      error,
      initPageIndex,
      gridPageChanged,
      queryParams,
      setQueryParams,
      refetch,
    } = query;

    const gridRef = useRef();
    if (!data && loading) {
      return (
        <LoadingOverlay active={loading} spinner text={'Παρακαλώ περιμένετε...'}>
          <section
            className="d-flex justify-content-center align-items-center"
            style={{ height: '300px' }}
          ></section>
        </LoadingOverlay>
      );
    } else if (!data) {
      return (
        <section
          className="d-flex justify-content-center align-items-center"
          style={{ height: '300px' }}
        >
          <h3 className="text-muted">{t('Δεν υπάρχουν προγραμματισμένα δρομολόγια!')}</h3>
        </section>
      );
    }
    if (error) {
      handleGraphqlError('query-get', error);
      return <h3 className="text-center">{error.message}</h3>;
    }
    return (
      <div>
        <GridPage
          ref={gridRef}
          loading={loading}
          data={data}
          collectionOrderId={collectionOrderId}
          dataCount={dataCount}
          initPageIndex={initPageIndex}
          onPageChange={gridPageChanged}
          initOrderBy={initOrderBy}
          onOrderChange={(orderBy) => {
            const currentOrder = queryParams.order || initOrderBy;
            let newOrder = orderBy;

            // Check if the current order is for the same column
            const [currentField, currentDir] = currentOrder.split(' ');
            const [newField] = orderBy.split(' ');

            if (currentField === newField) {
              newOrder = `${newField} ${currentDir === 'DESC' ? 'ASC' : 'DESC'}`;
            } else {
              newOrder = `${newField} DESC`; // default to DESC for new columns
            }

            setQueryParams({ ...queryParams, order: newOrder });
          }}
          onClose={onClose}
          refetch={refetch}
        />
      </div>
    );
  },
);
